import React from 'react';
import { useState } from 'react';

import { useAppConfig } from '@context/AppConfigContext.tsx';
import { useMiniAppSDK } from '@context/SDKConfigContext.tsx';
import { useSDKHandler } from '@features/method-tester/hooks/index.ts';
import fetchRequest from '@utils/fetchRequest.ts';

import { VERSION_0_3 } from '@/constant';
import { useJsBridgeVersion } from '@/context/JsBridgeVersionContext.tsx';
import { ERROR_CODE_MESSAGE_MAP } from '@/features/method-tester/constants';
import { OPERATION_KEY } from '@/features/method-tester/types/SDKCallResult.ts';
import { useFetchRequest } from '@/hooks';
import { Button } from '@asphalt-react/button';
import { Card } from '@asphalt-react/card';
import { Loader } from '@asphalt-react/loader';
import { Textfield } from '@asphalt-react/textfield';
import { Heading } from '@asphalt-react/typography';
import { OpenGopayDeeplinkResult } from '@gopay/miniapp-client';

/* Changes added here for callPing is just for the debugging purpose */

export const callPing = (baseURL: string) => {
  return fetchRequest<string>(`${baseURL}/ping`, {
    method: 'GET',
  });
};

export const DeepLink = () => {
  const [deepLink, setDeepLink] = useState<string>('gopay://scanqr');
  const {
    isLoading,
    response: deepLinkTriggerResp,
    invokeSDKActionV0_3,
    invokeSDKActionV1,
  } = useSDKHandler<OpenGopayDeeplinkResult>();
  const { VITE_BASE_API_URL: baseURL } = useAppConfig();
  const sdk = useMiniAppSDK();
  const { version } = useJsBridgeVersion();
  const [selectedErrorCode, setSelectedErrorCode] = useState<number | null>(
    null
  );
  const { error, data, request } = useFetchRequest<string>();

  const checkConnection = async () => {
    await request(() => callPing(baseURL));
  };

  const triggerDeeplink = async () => {
    try {
      const options = {
        ...(selectedErrorCode && {
          error: {
            operation_key: OPERATION_KEY.DEEPLINK,
            code: selectedErrorCode,
          },
        }),
      };
      if (version === VERSION_0_3) {
        await invokeSDKActionV0_3(
          () => sdk.openGopayDeeplink(deepLink),
          options
        );
      } else {
        await invokeSDKActionV1('GPNavigator', 'launchDeeplink', {
          deeplink: deepLink,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetSelection = () => setSelectedErrorCode(null);

  function onInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setDeepLink(event.target.value);
  }

  return (
    <Card>
      <div className="flex w-full justify-between flex-wrap">
        <Heading h6>Open DeepLink</Heading>
        <Button size="xs" onClick={triggerDeeplink} disabled={isLoading}>
          Open DeepLink
        </Button>
      </div>
      <div className="flex gap-4">
        {ERROR_CODE_MESSAGE_MAP.deeplink.map(({ code, message }) => (
          <label key={code} className="flex items-center gap-1">
            <input
              type="radio"
              name="ERROR_CODE"
              value={code}
              checked={selectedErrorCode === code}
              onChange={() => setSelectedErrorCode(code)}
            />
            {message}
          </label>
        ))}
      </div>
      <Button size="xs" onClick={resetSelection}>
        Reset Error Code
      </Button>
      <div className="pt-2">
        <Textfield
          type="text"
          placeholder="Enter the Deep Link"
          value={deepLink}
          onChange={onInputChange}
          stretch={true}
          size="s"
        ></Textfield>
      </div>

      {isLoading && <Loader />}
      {deepLinkTriggerResp && (
        <div className="mt-2 w-full">
          <pre
            data-testid="deeplink-response"
            className="overflow-auto text-sm bg-gray-100 p-2 rounded"
          >
            {JSON.stringify(deepLinkTriggerResp, null, 2)}
          </pre>
        </div>
      )}
      {data && <pre data-testid="ping-response">{JSON.stringify(data)}</pre>}
      {error && (
        <div data-testid="ping-error">Error: {JSON.stringify(error)}</div>
      )}
      <Button data-testid="call-api-button" onClick={checkConnection}>
        call API
      </Button>
    </Card>
  );
};
